/**
 * UNDO-TRASH-LIGHT (L1) — pila de deshacer de sesión, agnóstica de framework
 * y de dominio.
 *
 * Modelo: cada mutación de usuario registra su acción INVERSA como un cierre
 * (`undo`) que re-entra por el write-path normal (PowerSync/Drizzle) — nunca
 * manipulación directa de estado. Deshacer es una operación compensatoria
 * hacia adelante (estilo `git revert`): convive con offline y multi-dispositivo
 * porque el server la resuelve como cualquier otra escritura.
 *
 * La pila vive SOLO en memoria de la sesión de UI (se pierde al recargar; eso
 * es deliberado: para recuperar borrados persistidos está la papelera L2).
 *
 * Pieza COPIABLE tal cual a otras apps: no importa nada del dominio.
 */

export interface UndoEntry {
  /** Descripción corta de lo que se hizo (ya traducida), p. ej. "Hábito eliminado". */
  label: string;
  /** Aplica la mutación inversa por el write-path normal. */
  undo: () => Promise<void>;
}

export interface UndoStack {
  /** Registra una acción deshacible. Si se supera el límite, cae la más antigua. */
  push: (entry: UndoEntry) => void;
  /**
   * Deshace la última acción (LIFO). Devuelve la entrada deshecha o `null` si
   * la pila está vacía. Las llamadas concurrentes se serializan (una inversa
   * no arranca hasta que termina la anterior). Si la inversa falla, la entrada
   * se descarta igualmente (reintentar una inversa rota sería peor) y el error
   * se propaga al caller para que informe al usuario.
   */
  undoLast: () => Promise<UndoEntry | null>;
  size: () => number;
  /** Vacía la pila (obligatorio al cambiar de cuenta o cerrar sesión). */
  clear: () => void;
}

export const DEFAULT_UNDO_LIMIT = 50;

export function createUndoStack(opts: { limit?: number } = {}): UndoStack {
  const limit = Math.max(1, opts.limit ?? DEFAULT_UNDO_LIMIT);
  const entries: UndoEntry[] = [];
  // Serializa las inversas: Ctrl+Z repetido no debe solapar escrituras.
  let chain: Promise<unknown> = Promise.resolve();

  return {
    push(entry: UndoEntry): void {
      entries.push(entry);
      if (entries.length > limit) {
        entries.shift();
      }
    },
    undoLast(): Promise<UndoEntry | null> {
      const run = chain.then(async () => {
        const entry = entries.pop();
        if (!entry) {
          return null;
        }
        await entry.undo();
        return entry;
      });
      // La cadena nunca queda rechazada: el error solo viaja al caller de este undoLast.
      chain = run.catch(() => undefined);
      return run;
    },
    size: () => entries.length,
    clear(): void {
      entries.length = 0;
    },
  };
}
